/** API client for workflow persistence endpoints. */

import axios, { AxiosResponse } from 'axios';
import { auth } from '../lib/firebase';
import type {
  ScriptWorkflowState,
  WorkflowScriptListItem,
  WorkflowStepKey,
  WorkflowStepStatus,
} from '../types/workflow';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const workflowApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

workflowApi.interceptors.request.use(async (config) => {
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting Firebase token:', error);
    }
  }
  return config;
});

export async function listWorkflowScripts(): Promise<WorkflowScriptListItem[]> {
  const response: AxiosResponse<{ status: string; scripts: WorkflowScriptListItem[] }> =
    await workflowApi.get('/api/workflow/scripts');
  return response.data.scripts ?? [];
}

export async function getWorkflow(sessionId: string): Promise<ScriptWorkflowState> {
  const response: AxiosResponse<{ status: string; workflow: ScriptWorkflowState }> =
    await workflowApi.get(`/api/workflow/${sessionId}`);
  return response.data.workflow;
}

export async function patchWorkflowStep(
  sessionId: string,
  step: WorkflowStepKey,
  status: WorkflowStepStatus,
): Promise<ScriptWorkflowState> {
  const response: AxiosResponse<{ status: string; workflow: ScriptWorkflowState }> =
    await workflowApi.patch(`/api/workflow/${sessionId}`, {
      steps: { [step]: status },
    });
  return response.data.workflow;
}

export async function markGenerateScorecardComplete(
  sessionId: string,
): Promise<ScriptWorkflowState> {
  return patchWorkflowStep(sessionId, 'generate_scorecard', 'complete');
}
